import { io, Socket } from 'socket.io-client';

// Get socket URL from environment variables
const getSocketUrl = () => {
    if (process.env.REACT_APP_API_URL) {
        // Extract base URL from API URL (remove /api suffix only)
        const url = process.env.REACT_APP_API_URL;
        if (url.endsWith('/api')) {
            return url.slice(0, -4); // Remove '/api' suffix
        }
        return url;
    }
    return 'http://localhost:3001';
};

class SocketService {
    private socket: Socket | null = null;

    connect() {
        const socketUrl = getSocketUrl();
        this.socket = io(socketUrl);

        this.socket.on('connect', () => {
            console.log('🔌 Connected to backend socket');
        });

        this.socket.on('disconnect', () => {
            console.log('🔌 Disconnected from backend socket');
        });

        // Meeting bot events
        this.socket.on('bot-joined-meeting', (data) => {
            console.log('🤖 Bot joined meeting:', data);
            this.notifyBotJoined(data);
        });

        this.socket.on('real-time-transcription', (data) => {
            console.log('📝 Real-time transcription:', data);
            this.updateTranscription(data);
        });

        this.socket.on('clinical-analysis', (data) => {
            console.log('🩺 Clinical analysis:', data);
            this.updateClinicalAnalysis(data);
        });

        this.socket.on('bot-left-meeting', (data) => {
            console.log('🤖 Bot left meeting:', data);
            this.notifyBotLeft(data);
        });

        return this.socket;
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    // Listen for events
    on(event: string, callback: (data: any) => void) {
        if (this.socket) {
            this.socket.on(event, callback);
        }
    }

    // Remove event listeners
    off(event: string, callback?: (data: any) => void) {
        if (this.socket) {
            if (callback) {
                this.socket.off(event, callback);
            } else {
                this.socket.off(event);
            }
        }
    }

    // Emit events
    emit(event: string, data?: any) {
        if (this.socket) {
            this.socket.emit(event, data);
        }
    }

    // Join consultation room for real-time updates
    joinConsultation(consultationId: string) {
        if (this.socket) {
            this.socket.emit('join-consultation', consultationId);
        }
    }

    // Send transcription update
    sendTranscriptionUpdate(consultationId: string, transcription: string) {
        if (this.socket) {
            this.socket.emit('transcription-update', {
                consultationId,
                transcription,
                timestamp: new Date().toISOString(),
            });
        }
    }

    // Event handlers (can be overridden by components)
    private notifyBotJoined(data: any) {
        // Can be overridden by components
        window.dispatchEvent(new CustomEvent('bot-joined', { detail: data }));
    }

    private updateTranscription(data: any) {
        window.dispatchEvent(new CustomEvent('transcription-update', { detail: data }));
    }

    private updateClinicalAnalysis(data: any) {
        window.dispatchEvent(new CustomEvent('clinical-analysis', { detail: data }));
    }

    private notifyBotLeft(data: any) {
        window.dispatchEvent(new CustomEvent('bot-left', { detail: data }));
    }
}

export const socketService = new SocketService();
